import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { useAuth } from '../AuthContext'
import {
  ENQUIRIES_KEY,
  ENQUIRY_STATUSES,
  fetchEnquiries,
  type Enquiry,
  type EnquiryStatus,
} from '../api/enquiries'
import { SEARCHABLE } from '../searchable'
import { useServerSearch } from '../TableFilterContext'
import { DataTable, type Column } from '../components/DataTable'
import { EmptyState } from '../components/EmptyState'
import { FilterTabs, type FilterTab } from '../components/FilterTabs'
import { StatusPill } from '../components/StatusPill'

function relativeTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

function Detail({ enquiry }: { enquiry: Enquiry }) {
  const interest = [enquiry.specialty, enquiry.preferred_hospital]
    .filter(Boolean)
    .join(' · ')
  return (
    <details>
      <summary className="cursor-pointer text-ink-3">View</summary>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-ink-2">
        {interest && (
          <>
            <dt className="text-ink-3">Interest</dt>
            <dd>{interest}</dd>
          </>
        )}
        {enquiry.condition_description && (
          <>
            <dt className="text-ink-3">Condition</dt>
            <dd>{enquiry.condition_description}</dd>
          </>
        )}
        {enquiry.preferred_dates_text && (
          <>
            <dt className="text-ink-3">Dates</dt>
            <dd>{enquiry.preferred_dates_text}</dd>
          </>
        )}
        {enquiry.mobility && (
          <>
            <dt className="text-ink-3">Mobility</dt>
            <dd className="capitalize">
              {enquiry.mobility.replace(/_/g, ' ')}
            </dd>
          </>
        )}
        {enquiry.medical_history_url && (
          <>
            <dt className="text-ink-3">Records</dt>
            <dd>
              <a
                href={enquiry.medical_history_url}
                className="text-accent-ink underline"
                target="_blank"
                rel="noreferrer"
              >
                Open
              </a>
            </dd>
          </>
        )}
      </dl>
    </details>
  )
}

export function EnquiriesPage() {
  const { token } = useAuth()
  const [status, setStatus] = useState<EnquiryStatus | null>(null)

  const { term, setResultCount } = useServerSearch(SEARCHABLE.enquiries)
  const {
    data,
    isPending,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [...ENQUIRIES_KEY, status, term],
    queryFn: ({ pageParam }) =>
      fetchEnquiries(token, { cursor: pageParam, status, q: term }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.next_cursor,
    enabled: Boolean(token),
  })

  const rows = data?.pages.flatMap((p) => p.data) ?? []
  useEffect(() => {
    // The count is the rows LOADED so far — this queue is cursor-paginated, so
    // "6 results" here means six on the pages fetched, and the disclosure line
    // above the table already says more may follow.
    setResultCount(data === undefined ? null : rows.length)
  }, [data, rows.length, setResultCount])

  const tabs: FilterTab<EnquiryStatus | null>[] = [
    { key: null, label: 'All' },
    ...ENQUIRY_STATUSES.map((s) => ({
      key: s,
      label: s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    })),
  ]

  const columns: Column<Enquiry>[] = [
    {
      key: 'reference',
      header: 'Reference',
      cell: (e) => <span className="font-mono text-[13px]">{e.reference}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      cell: (e) => (
        <>
          <span className="block text-ink-1">{e.full_name}</span>
          <span className="block text-xs text-ink-3">{e.country_of_origin}</span>
        </>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      cell: (e) => (
        <>
          {/* The frame wraps every address across two lines mid-domain.
              Truncated with the full value on hover instead. */}
          <a
            href={`mailto:${e.email}`}
            title={e.email}
            className="block max-w-56 truncate text-accent-ink underline"
          >
            {e.email}
          </a>
          {e.phone && (
            <span className="block whitespace-nowrap text-xs text-ink-3">
              {e.phone}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      // A duration, not a date. The frame prints "5th Aug · 10:00 am" under a
      // column headed *Waiting* — the created date wearing the wrong label
      // (M23 defect D6).
      cell: (e) => (
        <time
          dateTime={e.created_at}
          title={new Date(e.created_at).toLocaleString()}
          className="whitespace-nowrap"
        >
          {relativeTime(e.created_at)}
        </time>
      ),
    },
    { key: 'status', header: 'Status', cell: (e) => <StatusPill status={e.status} /> },
    {
      key: 'detail',
      header: 'Details',
      // Inline, as it ships. T06's drawer is not a mandate to convert every
      // page to one.
      cell: (e) => <Detail enquiry={e} />,
    },
  ]

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Enquiries
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Tourism leads from the concierge form. Newest first.
        </p>
      </div>

      {/* Driven by ENQUIRY_STATUSES, not a literal list. No counts: the queue
          is cursor-paginated, so a bucket total would describe the loaded page
          rather than the queue. */}
      <FilterTabs tabs={tabs} active={status} onChange={setStatus} />

      <DataTable
        rows={rows}
        columns={columns}
        getKey={(e) => e.id}
        searchText={(e) =>
          `${e.reference} ${e.full_name} ${e.email} ${e.phone ?? ''} ${e.country_of_origin}`
        }
        caption="Captured tourism enquiries"
        isPending={isPending}
        isError={isError}
        errorMessage="Could not load enquiries."
        onRetry={() => void refetch()}
        paginated={hasNextPage === true}
        empty={
          <EmptyState
            title={status ? `No ${status.replace(/_/g, ' ')} enquiries.` : 'No enquiries yet.'}
          >
            {status
              ? 'Nothing in this status. Try All.'
              : 'Enquiries appear here as the concierge form is submitted.'}
          </EmptyState>
        }
        {...(hasNextPage === true && {
          footer: (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          ),
        })}
      />
    </section>
  )
}
